/**
 * Chapter 7 — Live narrates the day: the REAL Live card and decisions feed
 * fed by the fixture, frame by frame (not generated → waiting → running →
 * done) while the feed fills with the writers' own sentences. Reduced
 * motion shows the finished card, the full feed and a legend of the pills.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { ContainerLiveCard } from '@/features/drop-generator/components/live/ContainerLiveCard';
import { DecisionsFeed } from '@/features/drop-generator/components/live/DecisionsFeed';

import { DEMO_EVENTS, withRuntime, type RuntimeVariant } from '../../fixtures';
import { FrameStack } from '../../motion/FrameStack';
import { Illustration } from '../../motion/Illustration';

const CYCLE = 14;
/** Card state per frame, and how much of the feed has happened by then. */
const FRAMES: Array<{ variant: RuntimeVariant; events: number; label: string }> = [
  { variant: 'none', events: 0, label: 'before Generate' },
  { variant: 'waiting', events: 2, label: 'generated — nothing sent yet' },
  { variant: 'running', events: 6, label: 'sending — you pressed send on a few' },
  { variant: 'done', events: DEMO_EVENTS.length, label: 'the day is done — and judged' },
];
const LEGEND: Array<[string, string]> = [
  ['not generated yet', 'nobody pressed Generate for this day'],
  ['generated — waiting', 'drafts exist, none sent'],
  ['running', 'some sent, with the sent/planned count'],
  ['done HH:MM', 'every draft sent — the Danish time of the last'],
  ['stopped for today', 'a verdict stopped the rest of the day'],
  ['paused — pool exhausted', 'every list burned — Reset pool starts over'],
];

/** The real feed lists newest first — so does the story. */
const newestFirst = (count: number) => [...DEMO_EVENTS.slice(0, count)].reverse();

const Frame: FC<{ frame: (typeof FRAMES)[number] }> = ({ frame }) => (
  <div className="grid gap-3 md:grid-cols-2">
    <div className="flex flex-col gap-1">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{frame.label}</p>
      <ContainerLiveCard container={withRuntime(frame.variant).containers[0]} />
    </div>
    <DecisionsFeed events={newestFirst(frame.events)} loading={false} failed={false} />
  </div>
);

export const LiveDayIllustration: FC = () => (
  <Illustration
    cycleSeconds={CYCLE}
    finalFrame={
      <div className="flex flex-col gap-3">
        <Frame frame={FRAMES[FRAMES.length - 1]} />
        <dl className="grid gap-x-4 gap-y-1 text-xs md:grid-cols-2">
          {LEGEND.map(([pill, meaning]) => (
            <div key={pill} className="flex gap-2">
              <dt className="w-44 shrink-0 font-medium">{pill}</dt>
              <dd className="text-muted-foreground">{meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    }
  >
    <FrameStack cycleSeconds={CYCLE} frames={FRAMES.map((f) => <Frame key={f.variant} frame={f} />)} />
  </Illustration>
);
